import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Spinner } from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";

import useAudioController from "src/hooks/useAudioController";
import { useApp } from "src/components/App/Context";
import Dialog from "src/components/Dialog";
import { H4 } from "src/atoms/typography";
import ScrollBox from "src/atoms/ScrollBox";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import NavigationBar from "src/components/NavigationBar";
import LullabiesList from "src/modules/lullaby/components/LullabiesList";
import {
  getLibraryLullabies,
  addLibraryLullaby
} from "src/modules/lullaby/ajax/cloud";
import lullabyFiles from "src/modules/lullaby/lullaby-files";
import { getLullabiesLibrary } from "src/modules/lullaby/duck/selectors";
import {
  setLullabiesLibrary,
  saveLullaby
} from "src/modules/lullaby/duck/actions";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { usePurchaseContext } from "src/modules/purchase/PurchaseContext";
import useMemoizedCategories from "./useMemoizedCategories";

const ErrorType = {
  LOADING: "LOADING",
  ADDING: "ADDING"
};

export default function LullabiesLibrary({
  lullabies: usersLullabies,
  onBack,
  onClose
}) {
  const { t } = useTranslation();
  const purchase = usePurchaseContext();
  const dispatch = useDispatch();
  const app = useApp();
  const isFreeVersion = useSelector(isFreemium);
  const lullabiesLibrary = useSelector(getLullabiesLibrary);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const audioController = useAudioController({
    srcResolver: lullabyFiles.srcResolver
  });

  async function addLullabyFromLibrary(libraryIdentifier) {
    setIsSaving(true);
    try {
      const newLullaby = await addLibraryLullaby(libraryIdentifier);
      dispatch(saveLullaby(newLullaby));
      const { announceItemChange, SynchronizableItems } = app.synchronizer;
      announceItemChange(SynchronizableItems.LULLABIES);
    } catch (error) {
      setError(ErrorType.ADDING);
    } finally {
      setIsSaving(false);
    }
  }
  useEffect(() => {
    if (lullabiesLibrary.length === 0) {
      getLibraryLullabies()
        .then((lullabiesLibrary) =>
          dispatch(setLullabiesLibrary(lullabiesLibrary))
        )
        .catch((error) => setError(ErrorType.LOADING));
    }
  }, [lullabiesLibrary, dispatch, onBack]);

  const categories = useMemoizedCategories({
    lullabiesLibrary,
    usersLullabies
  });

  return (
    <>
      <GenericFailureDialog
        isOpen={audioController.hasPlayError}
        onDismiss={audioController.clearPlayError}
      />
      <GenericFailureDialog
        isOpen={Boolean(error)}
        onDismiss={error === ErrorType.LOADING ? onBack : () => setError(null)}
      />
      <Dialog
        isProcessing={isSaving}
        isOpen={isSaving}
        processingText={t("lullaby.addingHeader")}
      />
      <NavigationBar
        headingKey="lullaby.libraryHeader"
        onBack={onBack}
        onClose={onClose}
      />
      <Box py="16px" height="calc(100% - 61px)">
        <ScrollBox px="24px" height="100%">
          {lullabiesLibrary.length === 0 && (
            <Flex
              justifyContent="center"
              alignItems="center"
              pb="64px"
              height="100%"
            >
              <Spinner />
            </Flex>
          )}

          {categories.map(({ name, headingKey, lullabies }, index) => {
            return (
              <React.Fragment key={name}>
                <H4 mb="8px" mt={index === 0 ? 0 : "16px"} color="highEmphasis">
                  {t(headingKey)}
                </H4>
                <LullabiesList
                  lullabies={lullabies}
                  audioController={audioController}
                  onAdd={(identifier) =>
                    isFreeVersion
                      ? purchase.open({ content: "playAndRecord" })
                      : addLullabyFromLibrary(identifier)
                  }
                />
              </React.Fragment>
            );
          })}
        </ScrollBox>
      </Box>
    </>
  );
}
